import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { requestBonusEmail, type BonusEmailResult } from '@/services/loProfitabilityBonusEmailService';
import { BONUS_AWARD_LABELS, type ProfitabilityBonus } from '@/services/loProfitabilityBonusService';
import { endOfMonth, format, parseISO } from 'date-fns';

type ConfirmationProps = {
  bonus: ProfitabilityBonus;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
} & ({ context: 'postCreate'; currentMonth: string } | { context: 'rowMenu' });

export function BonusEmailConfirmation(props: ConfirmationProps) {
  const { bonus, context, onClose, onBusyChange } = props;
  const eligible = bonus.status === 'active' && (context === 'postCreate'
    ? bonus.award_month.slice(0, 7) < props.currentMonth.slice(0, 7) : bonus.is_effective);
  const heading = useRef<HTMLHeadingElement>(null);
  const flight = useRef(false);
  const [info, setInfo] = useState<BonusEmailResult | null>(null);
  const [loading, setLoading] = useState(Boolean(eligible));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [retryable, setRetryable] = useState(true);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    heading.current?.focus();
    let current = true;
    if (!eligible) {
      return;
    }
    requestBonusEmail(bonus.id, bonus.version, 'preview').then(result => {
      if (current) {
        setInfo(result);
        setSent(Boolean(result.alreadySent));
      }
    }).catch(caught => {
      if (current) {
        setError(caught instanceof Error ? caught.message : 'Email details are unavailable.');
        setRetryable((caught as { retryable?: boolean } | null)?.retryable !== false);
      }
    }).finally(() => {
      if (current) {
        setLoading(false);
      }
    });
    return () => {
      current = false;
    };
  }, [bonus.id, bonus.version, eligible]);

  const send = async () => {
    if (flight.current || !eligible || !retryable || !info?.enabled) {
      return;
    }
    flight.current = true;
    setSending(true);
    onBusyChange(true);
    setError('');
    try {
      const result = await requestBonusEmail(bonus.id, bonus.version, 'send');
      setInfo(result);
      setSent(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Email delivery could not be confirmed.');
      setRetryable((caught as { retryable?: boolean } | null)?.retryable !== false);
    } finally {
      flight.current = false;
      setSending(false);
      onBusyChange(false);
    }
  };
  return <div className="min-w-0 space-y-5">
    <DialogHeader>
      <DialogTitle ref={heading} tabIndex={-1} className="flex items-center gap-2 outline-none"><CheckCircle2 aria-hidden="true" className="h-5 w-5 shrink-0 text-success" />{context === 'postCreate' ? 'Bonus saved' : 'Congratulations email'}</DialogTitle>
      <DialogDescription>The profitability credit is saved. Sending congratulations is optional and does not change the bonus.</DialogDescription>
    </DialogHeader>
    <div className="rounded-md border bg-muted/30 p-3 text-sm">
      <p className="break-words font-medium">{bonus.recipient_name ?? info?.recipientName ?? 'Loan officer'}</p>
      <p className="mt-1 text-muted-foreground">{BONUS_AWARD_LABELS[bonus.award_type]} · {format(parseISO(bonus.award_month), 'MMMM yyyy')}</p>
      <p className="mt-2 font-semibold tabular-nums">{bonus.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} profitability credit</p>
    </div>
    {!eligible && <p role="status" className="text-sm">Send from the bonus's row menu once the award takes effect on {format(endOfMonth(parseISO(bonus.award_month)), 'MMM d, yyyy')} (Pacific)</p>}
    {loading ? <p role="status" className="text-sm text-muted-foreground">Checking email details…</p>
      : sent ? <p role="status" className="break-words text-sm text-success">Congratulations email {info?.alreadySent ? 'was already sent' : 'accepted for delivery'} to {info?.recipientEmail}.</p>
        : info && <div className="space-y-2 text-sm">
          <p className="break-words">Send a congratulations email to <span className="font-medium">{info.recipientEmail}</span>?</p>
          <p className="text-muted-foreground">Includes the award, winning month, photo when available and bonus amount.</p>
          {!info.enabled && <p className="text-warning">Recipient sending is disabled. Review the test email before enabling “LO of the Month — Congratulations” in Company Settings → Email Notifications.</p>}
        </div>}
    {info && <p className="break-words text-sm text-muted-foreground">
      {info.ccEmails?.length ? `CC: ${info.ccEmails.join(', ')}` : 'No one else is copied.'}
    </p>}
    {sent && info?.auditLogged === false && <p role="status" className="text-sm text-warning">The send receipt is saved, but Email Notifications activity could not be updated.</p>}
    {error && <p role="alert" className="break-words text-sm text-destructive">{error}</p>}
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button variant="outline" disabled={sending} onClick={onClose}>{sent || context === 'rowMenu' ? 'Done' : 'Keep saved without email'}</Button>
      {!sent && eligible && retryable && <Button disabled={loading || sending || !info?.enabled} className="h-auto min-h-10 whitespace-normal" onClick={() => void send()}>
        <Mail aria-hidden="true" className="mr-2 h-4 w-4 shrink-0" />{sending ? 'Sending…' : error ? 'Retry same email' : 'Send congratulations email'}
      </Button>}
    </div>
  </div>;
}
